import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Visibility, VisibilityOff, ExpandMore } from "@mui/icons-material";

/** --- Netflix-like palette tokens (local to component) --- */
const NF = {
  bg: "#141414",
  panel: "#0b0b0b",
  text: "#ffffff",
  muted: "rgba(255,255,255,0.78)",
  border: "rgba(255,255,255,0.14)",
  borderStrong: "rgba(255,255,255,0.22)",
  red: "#E50914",
  redHover: "#b20710",
  glow: "radial-gradient(900px 420px at 10% -10%, rgba(229,9,20,0.10), transparent 55%), radial-gradient(600px 300px at 110% 0%, rgba(255,255,255,0.05), transparent 60%)",
};

const SecuritySettings: React.FC = () => {
  const [show, setShow] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const toggle = () => setShow((s) => !s);

  const handleAccordionChange = (
    _event: React.SyntheticEvent,
    isExpanded: boolean
  ) => setExpanded(isExpanded);

  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box
      sx={{
        fontFamily: "'Lexend', sans-serif",
        color: NF.text,
      }}
    >
      <Accordion
        expanded={expanded}
        onChange={handleAccordionChange}
        sx={{
          boxShadow: "0 24px 60px rgba(0,0,0,.55)",
          borderRadius: 3,
          overflow: "hidden",
          background: `${NF.glow}, ${NF.bg}`,
          border: `1px solid ${NF.border}`,
          "&:before": { display: "none" },
          // improve touch target & spacing responsively
          ".MuiAccordionSummary-root": {
            px: { xs: 2, sm: 3 },
            py: { xs: 1.25, sm: 1.5 },
          },
          ".MuiAccordionDetails-root": {
            px: { xs: 2, sm: 3 },
            py: { xs: 2, sm: 2.5 },
            background:
              "linear-gradient(180deg, transparent, rgba(255,255,255,0.03))",
            borderTop: `1px solid ${NF.border}`,
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore htmlColor={NF.muted} />}
          sx={{
            "& .MuiAccordionSummary-content": {
              my: { xs: 0.25, sm: 0.5 },
            },
            "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
              transform: "rotate(180deg)",
            },
            transition: "background .2s ease",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.03)",
            },
          }}
        >
          <Stack spacing={0.75}>
            <Typography
              sx={{
                fontWeight: 900,
                letterSpacing: ".2px",
                textShadow: "0 1px 0 rgba(0,0,0,.35)",
                fontSize: { xs: "1.05rem", sm: "1.15rem" },
                color: NF.text,
              }}
            >
              Change Password
            </Typography>
            <Typography
              sx={{
                color: NF.muted,
                fontWeight: 400,
                fontSize: { xs: ".9rem", sm: ".95rem" },
              }}
            >
              Update your password to keep your account secure.
            </Typography>
          </Stack>
        </AccordionSummary>

        <AccordionDetails>
          <Stack spacing={{ xs: 1.5, sm: 2, md: 2.25 }}>
            {["Current Password", "New Password", "Confirm New Password"].map(
              (label) => (
                <TextField
                  key={label}
                  label={label}
                  type={show ? "text" : "password"}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={toggle}
                          edge="end"
                          sx={{
                            color: NF.muted,
                            "&:hover": { color: NF.text },
                          }}
                        >
                          {show ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiInputBase-root": {
                      borderRadius: 2,
                      backgroundColor: "rgba(255,255,255,0.04)",
                      color: NF.text,
                      "& fieldset": { borderColor: NF.border },
                      "&:hover fieldset": { borderColor: NF.borderStrong },
                      "&.Mui-focused fieldset": {
                        borderColor: NF.red,
                        boxShadow: "0 0 0 3px rgba(229,9,20,.22)",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: NF.muted,
                    },
                    "& .MuiFormHelperText-root": {
                      color: NF.muted,
                    },
                  }}
                />
              )
            )}

            <Stack
              direction={isSmDown ? "column" : "row"}
              spacing={isSmDown ? 1 : 1.5}
              alignItems={isSmDown ? "stretch" : "center"}
            >
              <Button
                variant="contained"
                fullWidth={isSmDown}
                sx={{
                  textTransform: "none",
                  fontWeight: 800,
                  letterSpacing: ".2px",
                  color: NF.text,
                  background: NF.red,
                  borderRadius: 2,
                  px: { xs: 2, sm: 3 },
                  py: { xs: 1, sm: 1.1 },
                  minHeight: { xs: 40, sm: 44 },
                  boxShadow: "0 10px 28px rgba(229,9,20,.35)",
                  "&:hover": { background: NF.redHover },
                }}
              >
                Update Password
              </Button>

              {/* Optional helper / policy hint */}
              <Typography
                sx={{
                  color: NF.muted,
                  fontSize: { xs: ".85rem", sm: ".9rem" },
                }}
              >
                Use at least 8 characters with a mix of letters & numbers.
              </Typography>
            </Stack>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default SecuritySettings;
